export interface ImageDimensions {
  width: number;
  height: number;
}

export interface DownscaleOptions {
  patchSize: number;
  maxNumPatches: number;
  epsilon: number;
  pixelShuffleScale: number;
  resultQuality: number;
}

function getScaledImageSize(scale: number, size: number, patchSize: number, pixelShuffleScale: number): number {
  // Scale, then snap **up** so the #patches per dim is divisible by pixelShuffleScale
  const divisor = patchSize * pixelShuffleScale;
  let scaledSize = size * scale;
  scaledSize = Math.ceil(scaledSize / divisor) * divisor;
  if (scaledSize < divisor) scaledSize = divisor;
  return scaledSize | 0; // fast int cast (keeps positive integers identical)
}

function getNumPatches(imageDimensions: ImageDimensions, patchSize: number, pixelShuffleScale: number): number {
  const { width: imageWidth, height: imageHeight } = imageDimensions;

  // Snap originals up to the nearest valid multiples
  const divisor = patchSize * pixelShuffleScale;

  let adjustedHeight = Math.ceil(imageHeight / divisor) * divisor;
  if (adjustedHeight < divisor) adjustedHeight = divisor;

  let adjustedWidth = Math.ceil(imageWidth / divisor) * divisor;
  if (adjustedWidth < divisor) adjustedWidth = divisor;

  return (adjustedHeight / patchSize) * (adjustedWidth / patchSize);
}

function calculateDownscaleDimensions(imageDimensions: ImageDimensions, options: DownscaleOptions): ImageDimensions {
  const { width: imageWidth, height: imageHeight } = imageDimensions;
  const { patchSize, maxNumPatches, epsilon: eps, pixelShuffleScale } = options;

  let numPatches = getNumPatches(imageDimensions, patchSize, pixelShuffleScale);

  if (numPatches <= maxNumPatches) {
    return imageDimensions; // no downscaling needed
  }

  let scaleMin = eps / 10;
  let scaleMax = 1.0;

  while (scaleMax - scaleMin >= eps) {
    const scale = (scaleMin + scaleMax) / 2;
    const targetHeight = getScaledImageSize(scale, imageHeight, patchSize, pixelShuffleScale);
    const targetWidth = getScaledImageSize(scale, imageWidth, patchSize, pixelShuffleScale);
    numPatches = (targetHeight / patchSize) * (targetWidth / patchSize);

    if (numPatches <= maxNumPatches) {
      scaleMin = scale;
    } else {
      scaleMax = scale;
    }
  }

  const scale = scaleMin;
  const targetHeight = getScaledImageSize(scale, imageHeight, patchSize, pixelShuffleScale);
  const targetWidth = getScaledImageSize(scale, imageWidth, patchSize, pixelShuffleScale);
  return { width: targetWidth, height: targetHeight };
}

/**
 * Create a canvas with specified dimensions and return its 2D context
 */
function createCanvasWithContext(
  width: number,
  height: number,
): {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
} {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }
  return { canvas, ctx };
}

/**
 * Convert canvas to blob - wraps the callback-based API in a Promise
 */
async function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to convert canvas to blob"));
        }
      },
      type,
      quality,
    );
  });
}

/**
 * Convert canvas to a File with appropriate format
 * Uses canvas.toBlob() for fast native encoding
 *
 * @param canvas - The canvas to convert
 * @param originalFile - The original file for metadata
 * @param quality - The quality of the image (0-1 scale)
 * @returns Promise resolving to the converted File
 */
async function convertCanvasToFile(canvas: HTMLCanvasElement, originalFile: File, quality: number): Promise<File> {
  switch (originalFile.type) {
    case "image/png":
    case "image/webp": {
      // Convert to WebP using canvas
      const blob = await canvasToBlob(canvas, "image/webp", quality);
      const fileName = originalFile.name.replace(/\.(png|webp)$/i, ".webp");

      return new File([blob], fileName, {
        type: "image/webp",
        lastModified: originalFile.lastModified,
      });
    }

    case "image/jpeg":
    case "image/jpg": {
      // Keep as JPEG using canvas
      const blob = await canvasToBlob(canvas, "image/jpeg", quality);

      return new File([blob], originalFile.name, {
        type: "image/jpeg",
        lastModified: originalFile.lastModified,
      });
    }
    default:
      throw new Error(`Unsupported file type: ${originalFile.type}`);
  }
}

/**
 * Load an image file and get its dimensions
 * @param file - Image file to load
 * @returns Promise resolving to image element and dimensions
 */
async function loadImage(file: File): Promise<{ img: HTMLImageElement; dimensions: ImageDimensions }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        img,
        dimensions: {
          width: img.width,
          height: img.height,
        },
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
}

/**
 * Downscale an image file using @jsquash/resize and convert to WebP
 * @param file - Image file to downscale
 * @param options - Downscaling options (defaults to patch_size=16, max_num_patches=6144)
 * @returns Promise resolving to downscaled and compressed file
 */
export async function downscaleAndConvertImage(file: File, options: DownscaleOptions): Promise<File> {
  // Only process image files
  if (!file.type.startsWith("image/")) {
    return file;
  }

  console.log("Downscaling image:", file.name, "mime type:", file.type, "size:", file.size, "bytes");

  try {
    // Load the image and get dimensions
    const { img, dimensions } = await loadImage(file);

    // Calculate if downscaling is needed
    const newDimensions = calculateDownscaleDimensions(dimensions, options);

    // If no downscaling needed, return original file
    if (newDimensions.width === dimensions.width && newDimensions.height === dimensions.height) {
      console.log("Image has dimensions:", dimensions.width, "x", dimensions.height, "and no downscaling needed");
      return file;
    }

    const { canvas: resizedCanvas, ctx: resizedCtx } = createCanvasWithContext(
      newDimensions.width,
      newDimensions.height,
    );

    // Use canvas's built-in scaling (GPU accelerated)
    // Using "medium" quality to match server's bilinear interpolation
    resizedCtx.imageSmoothingEnabled = true;
    resizedCtx.imageSmoothingQuality = "medium";
    resizedCtx.drawImage(img, 0, 0, newDimensions.width, newDimensions.height);

    // Convert canvas directly to file format using native browser encoding
    const finalFile = await convertCanvasToFile(resizedCanvas, file, options.resultQuality);

    console.log(
      "Downscaled image:",
      finalFile.name,
      "mime type:",
      finalFile.type,
      "size:",
      finalFile.size,
      "bytes",
      dimensions.width,
      "x",
      dimensions.height,
      "->",
      newDimensions.width,
      "x",
      newDimensions.height,
    );

    // Return the smaller file between original and processed
    if (finalFile.size >= file.size) {
      console.log("Downscaled image is larger than original image. Returning original file:", file.name);
      return file;
    }

    return finalFile;
  } catch (error) {
    console.error("Failed to downscale image:", error, " Returning original file.");
    return file;
  }
}
